import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService, UserRoles } from '../../../../services/auth.service';
import { SKILLS_MAX_PER_BOT, Skill, SkillService } from '../../../../services/skill.service';
import { Tutor, TutorService, TutorSettingItem } from '../../../../services/tutor.service';

interface SkillRow {
  skill: Skill;
  assigned: boolean;
}

/**
 * Agent › Skills: the switch (`agent_skills_enabled`), how many skills the
 * agent may load per answer (`agent_skills_max_loaded`), and which skills of
 * the library it has. Admins/managers pick from the whole library; everyone
 * else sees what is assigned.
 */
@Component({
  selector: 'app-tutor-skills',
  templateUrl: './tutor-skills.component.html',
  styleUrl: './tutor-skills.component.scss',
})
export class TutorSkillsComponent implements OnInit, OnDestroy {
  tutorId = '';
  tutor?: Tutor;
  enabledSetting?: TutorSettingItem;
  maxLoadedSetting?: TutorSettingItem;
  rows: SkillRow[] = [];
  loading = false;
  saving = false;
  readonly maxPerAgent = SKILLS_MAX_PER_BOT;

  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private skills: SkillService,
    private tutorService: TutorService,
    private auth: AuthService,
    private toastr: NbToastrService,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.tutorId =
      this.route.snapshot.paramMap.get('tutorId') ?? this.route.parent?.snapshot.paramMap.get('tutorId') ?? '';
    this.subs.push(
      this.tutorService.tutors$.subscribe(() => (this.tutor = this.tutorService.findTutor(this.tutorId))),
      this.tutorService.tutorSettings$.subscribe((settings) => {
        this.enabledSetting = (settings || []).find((s) => s.variable_name === 'agent_skills_enabled');
        this.maxLoadedSetting = (settings || []).find((s) => s.variable_name === 'agent_skills_max_loaded');
      })
    );
    this.load();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  get canAssign(): boolean {
    const role = this.auth.accessTokenPayload$.value?.role;
    return role === UserRoles.Admin || role === UserRoles.Manager || role === UserRoles.SuperAdmin;
  }

  get featureEnabled(): boolean {
    const v = this.enabledSetting?.value ?? this.enabledSetting?.default_value;
    return v === true || v === 'true';
  }

  get assignedCount(): number {
    return this.rows.filter((r) => r.assigned).length;
  }

  load(): void {
    this.loading = true;
    const all$ = this.canAssign ? this.skills.list() : this.skills.listForBot(this.tutorId);
    all$.subscribe({
      next: (list) => {
        this.rows = list.map((s) => ({ skill: s, assigned: s.assigned_bot_ids.includes(this.tutorId) }));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastr.danger(this.t.instant('pages.skills.errors.loadFailed'), '');
      },
    });
  }

  toggleAssigned(row: SkillRow, assigned: boolean): void {
    const previous = row.assigned;
    row.assigned = assigned; // mirror first (CLAUDE.md rule 17)
    const ids = this.rows.filter((r) => r.assigned).map((r) => r.skill.id);
    this.saving = true;
    this.skills.assignToBot(this.tutorId, ids).subscribe({
      next: () => {
        this.saving = false;
        this.toastr.success(this.t.instant('pages.skills.agent.saved'), '');
      },
      error: (err) => {
        this.saving = false;
        row.assigned = previous;
        const detail = err?.error?.detail;
        this.toastr.danger(typeof detail === 'string' ? detail : this.t.instant('pages.skills.errors.saveFailed'), '');
      },
    });
  }

  trackRow = (_: number, row: SkillRow) => row.skill.id;
}
